<template>
  <div class="payment-method-cards" data-test="payment-method-details">
    <div
      class="iframeHolder"
      ref="iframeHolder"
      :class="iframeStep ? 'visible' : 'invisible'"
    ></div>
    <error-notification :errorCode="pageDetails.errorCode"></error-notification>
    <ValidationObserver ref="observer" v-if="cardStep">
      <div class="row">
        <div v-if="!cse" class="col-sm-12 text-center">
          <span v-if="!cse"
            >Something went wrong, you're not able to pay with Credit Card.<br />Please
            try again later.</span
          >
        </div>
        <div v-if="cse" class="col-sm-12 text-left">
          <div class="row">
            <div
              class="col available-cards text-center"
              data-test="available-cards-logos"
            >
              <img
                alt="American Express"
                v-if="amexAllowed"
                src="../../assets/cards/amex.svg"
              />
              <img
                alt="Mastercard"
                v-if="masterCardAllowed"
                src="../../assets/cards/mastercard.svg"
              />
              <img
                alt="VISA"
                v-if="visaAllowed"
                src="../../assets/cards/visa.svg"
              />
            </div>
          </div>
          <form>
            <legend>{{ $t("paymentdetails.cards.title") }}</legend>
            <div class="row">
              <div class="col-sm-12">
                <ValidationProvider rules="required">
                  <card-holder
                    slot-scope="{ errors, flags }"
                    v-model="form.nameOnCard"
                    :messages="errors"
                    :flags="flags"
                  ></card-holder>
                </ValidationProvider>
                <ValidationProvider
                  :rules="
                    'required|luhn|allowed:' +
                    pageDetails.allowedIssuers +
                    ',' +
                    form.issuer
                  "
                >
                  <card-number
                    @updatecc="updateCCValues"
                    slot-scope="{ errors, flags }"
                    v-model="form.cardNumber"
                    :messages="errors"
                    :flags="flags"
                  ></card-number>
                </ValidationProvider>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-4">
                <ValidationProvider rules="required|is_not:MM">
                  <expiration-month
                    slot-scope="{ errors, flags }"
                    v-model="form.expirationMonth"
                    :messages="errors"
                    :flags="flags"
                  ></expiration-month>
                </ValidationProvider>
              </div>
              <div class="col-sm-4">
                <ValidationProvider rules="required|is_not:YYYY">
                  <expiration-year
                    slot-scope="{ errors, flags }"
                    v-model="form.expirationYear"
                    :messages="errors"
                    :flags="flags"
                  ></expiration-year>
                </ValidationProvider>
              </div>
              <div class="col-sm-4">
                <ValidationProvider rules="required|numeric">
                  <cvv
                    slot-scope="{ errors, flags }"
                    v-model="form.cvv"
                    :messages="errors"
                    :flags="flags"
                  ></cvv>
                </ValidationProvider>
              </div>
            </div>
          </form>
        </div>
      </div>
    </ValidationObserver>
    <box-footer v-if="cardStep">
      <button
        v-if="cse"
        class="btn btn-cta"
        @click="pay()"
        data-test="pay-button"
      >
        {{ $t("paymentdetails.pay") }}
      </button>
    </box-footer>
  </div>
</template>

<script>
import errorNotification from "@/components/form/error-notification.vue";
import cardNumber from "@/components/form/card-number.vue";
import cardHolder from "@/components/form/card-holder.vue";
import expirationMonth from "@/components/form/expiration-month.vue";
import expirationYear from "@/components/form/expiration-year.vue";
import cvv from "@/components/form/cvv.vue";

import boxFooter from "@/components/box-footer.vue";

import { ValidationProvider, ValidationObserver } from "vee-validate";
import "@/utils/validator-luhn.js";
import "@/utils/validator-allowed.js";

import { createNamespacedHelpers } from "vuex";
const paymentNS = createNamespacedHelpers("payment");

export default {
  name: "paymentMethodCards",
  data() {
    return {
      form: {
        nameOnCard: "",
        issuer: "",
        cardNumber: "",
        expirationMonth: "MM",
        expirationYear: "YYYY",
        cvv: null,
      },
      step: {
        iframe: false,
        card: true,
      },
      cse: false,
      interval: null,
    };
  },
  components: {
    errorNotification,
    boxFooter,
    ValidationProvider,
    ValidationObserver,
    cardNumber,
    cardHolder,
    expirationMonth,
    expirationYear,
    cvv,
  },
  created() {
    if (this.pageDetails.clientSideEncryption) {
      if (this.pageDetails.clientSideEncryption.upstream === "PS") {
        if (
          this.pageDetails.clientSideEncryption.javaScript !== null &&
          Array.isArray(this.pageDetails.clientSideEncryption.javaScript)
        ) {
          this.pageDetails.clientSideEncryption.javaScript.forEach((value) => {
            console.log(value);
            let externalScript = document.createElement("script");
            externalScript.setAttribute("src", value);
            document.head.appendChild(externalScript);
          });
          this.interval = setInterval(() => {
            if (typeof cseEncrypt === "function") {
              this.cse = true;
              clearInterval(this.interval);
            }
          }, 100);
        }
      }
    }
  },
  computed: {
    ...paymentNS.mapGetters(["pageDetails", "page"]),
    visaAllowed() {
      if (this.pageDetails.allowedIssuers) {
        return this.pageDetails.allowedIssuers.includes("VISA");
      } else {
        return false;
      }
    },
    masterCardAllowed() {
      if (this.pageDetails.allowedIssuers) {
        return this.pageDetails.allowedIssuers.includes("MASTERCARD");
      } else {
        return false;
      }
    },
    amexAllowed() {
      if (this.pageDetails.allowedIssuers) {
        return this.pageDetails.allowedIssuers.includes("AMEX");
      } else {
        return false;
      }
    },
    cardStep() {
      return this.step.card;
    },
    iframeStep() {
      return this.step.iframe;
    },
  },
  methods: {
    ...paymentNS.mapActions(["updateState"]),
    ...paymentNS.mapMutations(["setLoading", "setErrorCode"]),
    iframeToggle() {
      this.step.card = !this.step.card;
      this.step.iframe = !this.step.iframe;
    },
    updateCCValues(value) {
      this.form.cardNumber = value.cardNumber;
      this.form.issuer = value.cardType;
    },
    async pay() {
      this.setErrorCode(null);
      if (this.cse) {
        const encrypted = cseEncrypt(
          this.form.nameOnCard,
          this.form.cardNumber.replace(/ /g, ""),
          this.form.expirationMonth,
          this.form.expirationYear,
          this.form.cvv
        );
        console.log(encrypted);
        const params = {
            path: "provide-payment-details",
            data: {
              paymentMethod: "Card",
              paymentDetails: {
                issuer: this.form.issuer.toUpperCase(),
                encryptedData: encrypted,
              },
            },
            iframeHolder: this.$refs.iframeHolder,
            callbackToggle: this.iframeToggle,
          },
          isValid = await this.$refs.observer.validate();
        if (isValid) this.updateState(params);
      }
    },
  },
};
</script>

<style scoped>
.iframeHolder {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 1;
}
</style>
